import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, Watch } from "@stencil/core";

import { hasSlot } from "../../utils/utils";

import { StzhCheckboxChangeEvent, StzhCheckboxgroupChangeEvent, StzhLocaleComponent } from "../../index";
import { camelCase } from "../../utils/string-utils";

import { StzhInputDescription } from "../stzh-input/stzh-input-description";

let checkboxgroupCounter = 0;

/**
 * @slot - Slot for `stzh-checkbox` elements
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot description-long-title - Slot for long description title (in popover) / instead of descriptionLongTitle or description property
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-checkboxgroup",
  styleUrl: "stzh-checkboxgroup.scss",
  scoped: true,
})
export class StzhCheckboxgroup {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** The name of the input checkbox elements */
  @Prop({ reflect: true }) name: string;

  /** The legend */
  @Prop() legend: string;

  /** Hide legend to show only for screenreaders */
  @Prop({ reflect: true }) hideLegend: boolean = false;

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Hide `(optional)` label */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** The selected values of the checkboxgroup  */
  @Prop({ mutable: true }) value: string[] | string = "";
  private _value: string[] = [];

  /** Default value (used by reset) */
  @Prop({ mutable: true }) defaultValue: string[] = [];

  /** Direction */
  @Prop({ reflect: true }) direction: "vertical" | "horizontal" = "vertical";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /**
   * **Do not use this property. It will be deprecated with the next major release.**
   * Long description title appearing in a popover (use description-long-title slot as or description property alternative) */
  @Prop() descriptionLongTitle: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Current checked checkbox elements (readonly) */
  @Prop() checkedCheckbox: HTMLStzhCheckboxElement[] | null = null;

  /** Prevent updating certain properties of radio elements (possible values inside array: disabled, invalid) */
  @Prop() preventUpdateProperties: string[] | string = [];
  private _preventUpdateProperties: string[] = [];

  /** Host element */
  @Element() element: HTMLElement;

  @Watch("name")
  watchName(newValue: string) {
    this.updateCheckboxesProperty("name", newValue);
  }

  @Watch("disabled")
  watchDisabled(newValue: boolean) {
    this.updateCheckboxesProperty("disabled", newValue);
  }

  @Watch("value")
  watchValue(newValue: string[] | string) {
    if (typeof newValue === "string") {
      this._value = JSON.parse(newValue);
    } else {
      this._value = newValue;
    }

    if (JSON.stringify(this._value) !== JSON.stringify(this.value)) {
      this.value = this._value;
    }

    this.updateCheckedCheckboxByValue(this._value);
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  @Watch("preventUpdateProperties")
  preventUpdatePropertiesWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      this._preventUpdateProperties = JSON.parse(newValue);
    } else {
      this._preventUpdateProperties = newValue;
    }
  }

  /** Change event */
  @Event() stzhChange: EventEmitter<StzhCheckboxgroupChangeEvent>;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Listen("stzhChange", { capture: true })
  onChange(event: CustomEvent<StzhCheckboxChangeEvent>) {
    if (event.detail.component === "stzh-checkbox") {
      if (event.detail.checked) {
        this._value.push(event.detail.value);
      } else {
        this._value = this._value.filter(value => value !== event.detail.value);
      }
      // stop propagating checkbox change event otherwise angular control value accessor won't work
      event.stopPropagation();
      this.value = this._value;
      this.stzhChange.emit({
        component: "stzh-checkboxgroup",
        value: this._value,
      });
    }
  }

  private get errorUsed() {
    return hasSlot(this.element, "error") || this._error?.length > 0;
  }

  /** Checkbox elements */
  private checkboxes: HTMLStzhCheckboxElement[];
  private checkboxgroupId: string;
  private observer: MutationObserver;

  private updateCheckboxesProperty(prop: string, value: any) {
    this.checkboxes.forEach((checkbox: HTMLStzhCheckboxElement) => {
      if (prop === "disabled") {
        // Only set the disabled if the checkbox is not already disabled
        if (!checkbox.disabled) {
          checkbox.disabled = value;
        }
      } else if (prop === "name" || this._preventUpdateProperties.indexOf(prop) === -1) {
        checkbox[camelCase(prop)] = value;
      }
    });
  }

  private updateCheckedCheckboxByValue(value: string[]) {
    this.checkedCheckbox = this.checkboxes.filter(checkbox => value.indexOf(checkbox.value) > -1);
    this.checkboxes.forEach(checkbox => (checkbox.checked = value.indexOf(checkbox.value) > -1));
  }

  private handleReset = async () => {
    this.value = [...this.defaultValue];
  };

  private init = () => {
    // update checkboxes
    this.checkboxes = Array.from(this.element.querySelectorAll("stzh-checkbox"));

    this.updateCheckboxesProperty("hide-optional", true);
    this.updateCheckboxesProperty("a11y-describedby", `${this.checkboxgroupId}-description`);
    this.updateCheckboxesProperty("invalid", this.invalid || this.errorUsed);

    this.watchValue(this.value);
    this.watchName(this.name);
    this.watchDisabled(this.disabled);
  };

  async componentWillLoad() {
    this.checkboxgroupId = `stzh-checkboxgroup-${checkboxgroupCounter++}`;
    this.errorWatcher(this.error);
    this.preventUpdatePropertiesWatcher(this.preventUpdateProperties);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "checkboxgroup");
    }
  }

  componentShouldUpdate(_newValue, _oldValue, prop: string) {
    return prop !== "checkedCheckbox";
  }

  componentDidRender() {
    this.init();
  }

  componentDidLoad() {
    this.defaultValue = this.defaultValue.length > 0 ? this.defaultValue : [...this._value];
  }

  connectedCallback() {
    if (!this.value.length) {
      this.checkboxes = Array.from(this.element.querySelectorAll("stzh-checkbox"));

      // try to find checkboxes that might have checked set to true
      this.value = this.checkboxes.filter(checkbox => checkbox.checked).map(checkbox => checkbox.value);
    }

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render() {
    const descriptionUsed = hasSlot(this.element, "description") || !!this.description;
    const descriptionLongUsed = hasSlot(this.element, "description-long") || !!this.descriptionLong;

    const classes = {
      "stzh-checkboxgroup": true,
      "stzh-checkboxgroup--has-description": descriptionUsed,
      "stzh-checkboxgroup--has-description-long": descriptionLongUsed,
      "stzh-checkboxgroup--has-error": this.errorUsed,
      "stzh-checkboxgroup--is-disabled": this.disabled,
      "stzh-checkboxgroup--is-invalid": this.invalid || this.errorUsed,
      "stzh-checkboxgroup--hide-legend": this.hideLegend,
      [`stzh-checkboxgroup--direction-${this.direction}`]: !!this.direction,
    };

    return (
      <Host is-invalid={this.invalid || this.errorUsed}>
        <fieldset class={classes}>
          {this.legend && (
            <legend class="stzh-checkboxgroup__legend">
              {this.legend}
              {!this.hideOptional && (
                <span class="stzh-checkboxgroup__marker">
                  <span class="stzh-checkboxgroup__marker-symbol" aria-hidden="true">
                    {this.required
                      ? this.localization.$globals.requiredFieldMarker
                      : this.localization.$globals.optionalFieldMarker}
                  </span>
                  <span class="stzh-checkboxgroup__marker-text">
                    {this.required
                      ? this.localization.$globals.requiredFieldText
                      : this.localization.$globals.optionalFieldText}
                  </span>
                </span>
              )}
            </legend>
          )}
          <div class="stzh-checkboxgroup__fields">
            <slot></slot>
          </div>
          {(this.errorUsed || this.description) && (
            <StzhInputDescription
              classPrefix="stzh-checkboxgroup"
              id={`${this.checkboxgroupId}-description`}
              error={this._error}
              description={this.description}
              descriptionLong={this.descriptionLong}
              descriptionLongTitle={this.descriptionLongTitle}
              descriptionLongUsed={descriptionLongUsed}
              moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
            />
          )}
        </fieldset>
      </Host>
    );
  }
}
